// Drag for a thumb: hold a row, it lifts, carry it to a lane, a pad or the
// "New track" row, let go. HTML5 drag never fires for a finger, so this is the
// phone's drag — pointer events on the document after a hold, a ghost that
// follows the finger, and DOM events on whatever drop target is under it.
//
// Targets are found by attribute, not by React: a lane row carries
// `data-drop-lane="<laneId>"`, an empty pad `data-drop-slot="<laneId>:<row>"`,
// the new-track row `data-drop-new-track`. The lift dispatches `liftover` /
// `liftleave` / `liftdrop` CustomEvents (bubbling) on the target element, with
// the pointer position and the payload in `detail`; the component owning the
// target listens with a ref and routes to the same handler its HTML5 drop
// uses. So a touch drop and a mouse drop are one code path past this file.
//
// Why the browser lets us: the hold happened without movement, so the
// browser has not committed the touch to a scroll. From the lift on, every
// `touchmove` is `preventDefault()`ed (non-passive), which keeps the page
// still and the pointer events flowing. A finger that scrolled before the
// hold fired never gets here — `useHold` cancels on 10 px of travel.

import type { MaterialPayload } from "@web-shared/lib/material-item";

export const DROP_LANE_ATTR = "data-drop-lane";
export const DROP_SLOT_ATTR = "data-drop-slot";
export const DROP_NEW_TRACK_ATTR = "data-drop-new-track";
const TARGET_SELECTOR = `[${DROP_LANE_ATTR}],[${DROP_SLOT_ATTR}],[${DROP_NEW_TRACK_ATTR}]`;

export const LIFT_OVER = "seedbed:liftover";
export const LIFT_LEAVE = "seedbed:liftleave";
export const LIFT_DROP = "seedbed:liftdrop";

export interface LiftDetail {
  clientX: number;
  clientY: number;
  payload: MaterialPayload;
}

export type LiftEvent = CustomEvent<LiftDetail>;

/** The drop target under a point, if any. */
export function dropTargetAt(x: number, y: number): Element | null {
  const el = document.elementFromPoint(x, y);
  return el?.closest(TARGET_SELECTOR) ?? null;
}

/** The slot a `data-drop-slot` names. */
export function parseSlot(value: string): { laneId: string; row: number } | null {
  const at = value.lastIndexOf(":");
  if (at < 0) return null;
  const row = Number(value.slice(at + 1));
  return Number.isInteger(row) ? { laneId: value.slice(0, at), row } : null;
}

export interface LiftOptions {
  payload: MaterialPayload;
  /** What the ghost shows. */
  label: string;
  color: string;
  /** The pointer that is down; moves of other pointers are ignored. */
  pointerId: number;
  /** Where the finger is now, so the ghost appears under it at once. */
  clientX: number;
  clientY: number;
  /** Called once, after the drop (true) or a cancel (false). */
  onEnd: (dropped: boolean) => void;
  /** Called as the target under the finger changes, for the shelf to react
   * (e.g. collapse to its peek while a drag is on). */
  onTargetChange?: (target: Element | null) => void;
}

const EDGE_PX = 40;
const EDGE_SPEED_PX = 14;

/**
 * Start a lift. Returns a function that cancels it (the drag's own end
 * removes everything itself).
 */
export function startLift(options: LiftOptions): () => void {
  const ghost = document.createElement("div");
  ghost.setAttribute("aria-hidden", "true");
  ghost.style.cssText = [
    "position:fixed",
    "z-index:1000",
    "pointer-events:none",
    "max-width:60vw",
    "padding:8px 12px",
    "border-radius:12px",
    "font:600 12px/1.2 system-ui,sans-serif",
    "color:#111",
    "box-shadow:0 8px 24px rgba(0,0,0,.45)",
    "transform:translate(-50%,-120%) scale(1.04)",
    "white-space:nowrap",
    "overflow:hidden",
    "text-overflow:ellipsis",
    `background:${options.color}`,
  ].join(";");
  ghost.textContent = options.label;
  document.body.appendChild(ghost);

  let over: Element | null = null;
  let raf = 0;
  let last = { x: options.clientX, y: options.clientY };
  let ended = false;

  const place = (x: number, y: number): void => {
    ghost.style.left = `${x}px`;
    ghost.style.top = `${y}px`;
  };
  const detail = (x: number, y: number): LiftDetail => ({ clientX: x, clientY: y, payload: options.payload });
  const fire = (el: Element, type: string, x: number, y: number): void => {
    el.dispatchEvent(new CustomEvent<LiftDetail>(type, { bubbles: true, detail: detail(x, y) }));
  };
  const setOver = (next: Element | null, x: number, y: number): void => {
    if (next === over) {
      if (over) fire(over, LIFT_OVER, x, y);
      return;
    }
    if (over) fire(over, LIFT_LEAVE, x, y);
    over = next;
    if (over) fire(over, LIFT_OVER, x, y);
    options.onTargetChange?.(over);
  };

  /** Near a scrollable edge, nudge that scroller — a held clip does the same on the timeline. */
  const autoScroll = (): void => {
    if (ended) return;
    const { x, y } = last;
    const under = document.elementFromPoint(x, y);
    const scroller = under ? nearestScroller(under) : null;
    if (scroller) {
      const box = scroller.getBoundingClientRect();
      if (scroller.scrollWidth > scroller.clientWidth) {
        if (x < box.left + EDGE_PX) scroller.scrollLeft -= EDGE_SPEED_PX;
        else if (x > box.right - EDGE_PX) scroller.scrollLeft += EDGE_SPEED_PX;
      }
      if (scroller.scrollHeight > scroller.clientHeight) {
        if (y < box.top + EDGE_PX) scroller.scrollTop -= EDGE_SPEED_PX;
        else if (y > box.bottom - EDGE_PX) scroller.scrollTop += EDGE_SPEED_PX;
      }
    }
    raf = requestAnimationFrame(autoScroll);
  };

  const onMove = (e: PointerEvent): void => {
    if (e.pointerId !== options.pointerId) return;
    last = { x: e.clientX, y: e.clientY };
    place(e.clientX, e.clientY);
    // The ghost is pointer-events:none, so the point hits what is beneath it.
    setOver(dropTargetAt(e.clientX, e.clientY), e.clientX, e.clientY);
  };
  const blockScroll = (e: TouchEvent): void => {
    if (e.cancelable) e.preventDefault();
  };
  const end = (dropped: boolean, x: number, y: number): void => {
    if (ended) return;
    ended = true;
    cancelAnimationFrame(raf);
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
    document.removeEventListener("pointercancel", onCancel);
    document.removeEventListener("touchmove", blockScroll);
    window.removeEventListener("keydown", onKey);
    ghost.remove();
    if (over) {
      if (dropped) fire(over, LIFT_DROP, x, y);
      else fire(over, LIFT_LEAVE, x, y);
    }
    options.onTargetChange?.(null);
    options.onEnd(dropped && over !== null);
  };
  const onUp = (e: PointerEvent): void => {
    if (e.pointerId !== options.pointerId) return;
    end(true, e.clientX, e.clientY);
  };
  const onCancel = (e: PointerEvent): void => {
    if (e.pointerId !== options.pointerId) return;
    end(false, e.clientX, e.clientY);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") end(false, last.x, last.y);
  };

  place(options.clientX, options.clientY);
  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onUp);
  document.addEventListener("pointercancel", onCancel);
  document.addEventListener("touchmove", blockScroll, { passive: false });
  window.addEventListener("keydown", onKey);
  raf = requestAnimationFrame(autoScroll);

  return () => end(false, last.x, last.y);
}

function nearestScroller(from: Element): HTMLElement | null {
  let el: Element | null = from;
  while (el && el !== document.body) {
    if (el instanceof HTMLElement) {
      const { overflowX, overflowY } = getComputedStyle(el);
      const scrolls = (v: string) => v === "auto" || v === "scroll";
      if ((scrolls(overflowX) && el.scrollWidth > el.clientWidth) || (scrolls(overflowY) && el.scrollHeight > el.clientHeight)) return el;
    }
    el = el.parentElement;
  }
  return null;
}
